/** Planer i RCP — teksty obszaru RCP po polsku (`pln.worktime.*`): panel RCP, mój czas pracy, reguły czasu pracy. */
export type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const plnWorktimePl = {
  title: 'Rejestracja czasu pracy',
  description: 'Karta czasu pracy wg Kodeksu pracy: plan z grafiku, wykonanie z odbić, wizyt i kart drogowych, korekty kierownika',
  hero: {
    inWorkNow: 'W pracy teraz',
    worked: 'Przepracowane w miesiącu',
    overtime: 'Nadgodziny',
    alerts: 'Alerty KP',
    openOverLimit: 'Odbicia ponad 16 h',
    openOverLimitHint: 'Otwarte odbicia dłuższe niż 16 h — wymagają korekty kierownika',
  },
  tabs: {
    vacation: 'Urlopy',
    team: 'Teraz w pracy',
    card: 'Karta pracownika',
    collective: 'Zestawienie',
    periods: 'Okresy rozliczeniowe',
    profiles: 'Profile',
  },
  month: { prev: 'Poprzedni miesiąc', next: 'Następny miesiąc', current: 'Bieżący' },
  export: { pdf: 'Karta (PDF)', xlsx: 'Zestawienie (XLSX)', done: 'Pobrano plik', failed: 'Nie udało się pobrać pliku' },
  team: {
    title: 'Kto jest teraz w pracy',
    description: 'Odbicia z apki i panelu na żywo, z planem z grafiku na dziś',
    filterAll: 'Wszyscy',
    filterIn: 'W pracy',
    filterOut: 'Poza pracą',
    search: 'Szukaj osoby lub działu…',
    colPerson: 'Osoba',
    colStatus: 'Status',
    colPlan: 'Plan na dziś',
    colSource: 'Źródło dziś',
    inWorkSince: 'W pracy od {time}',
    elapsed: '{duration} h',
    overLimit: 'ponad 16 h',
    outOfWork: 'Poza pracą',
    noPlan: 'Bez zmiany',
    openCard: 'Karta',
    empty: 'Nikt nie pasuje do filtra',
    emptyAll: 'Brak osób w zespole',
  },
  card: {
    people: 'Pracownicy',
    searchPeople: 'Szukaj pracownika…',
    noPeople: 'Brak pracowników w Twoim zakresie',
    noMatch: 'Nikt nie pasuje do wyszukiwania',
    pick: 'Wybierz pracownika z listy',
    balanceTitle: 'Saldo miesiąca (h)',
    profileLine: '{system} · etat {etat} · norma {daily} h/dzień',
    cols: {
      date: 'Data',
      plan: 'Plan',
      exec: 'Wykonanie',
      source: 'Źródło',
      worked: 'Przepr.',
      ot50: 'N 50%',
      ot100: 'N 100%',
      night: 'Noc',
      sundayHoliday: 'Nd/Św',
      absence: 'Nieob.',
      notes: 'Uwagi',
    },
    colsHint: {
      ot50: 'Nadgodziny z dodatkiem 50%',
      ot100: 'Nadgodziny z dodatkiem 100% (noc, niedziela, święto, dzień wolny)',
      night: 'Godziny w porze nocnej',
      sundayHoliday: 'Godziny w niedziele i święta',
      absence: 'Godziny nieobecności (liczone do wymiaru)',
    },
    correct: 'Korekta dnia',
    adjustments: 'Korekt: {count}',
    today: 'Dziś',
    total: 'Razem',
    legend: {
      title: 'Legenda',
      sunday: 'Niedziela',
      holiday: 'Święto',
      absence: 'Nieobecność',
      free: 'Dzień wolny',
      today: 'Dziś',
    },
  },
  summary: {
    nominal: 'Wymiar do wypracowania',
    nominalHint: 'Wymiar KP minus nieobecności: {raw} h − {absence} h',
    worked: 'Przepracowane',
    workedPct: '{pct}% wymiaru',
    balance: 'Saldo okresu',
    balanceHint: 'Przepracowane minus wymiar',
    balanceAsOf: 'Stan na koniec {date} — wymiar i praca do wczoraj',
    overtimeLimit: 'Limit nadgodzin',
    overtimeLimitHint: '{used} z {limit} h rocznie',
    ot50: 'Nadgodziny 50%',
    ot100: 'Nadgodziny 100%',
    night: 'Pora nocna',
    sundayHoliday: 'Niedziele i święta',
    absence: 'Nieobecności',
    onCall: 'Dyżury',
  },
  alerts: {
    title: 'Alerty zgodności z Kodeksem pracy',
    none: 'Brak naruszeń w tym miesiącu',
    period: 'Cały okres',
    critical: 'Naruszenie',
    warn: 'Ostrzeżenie',
  },
  correction: {
    title: 'Korekta dnia',
    plan: 'Plan',
    planHint: 'Godziny z grafiku — zmieniaj tylko, gdy grafik był błędny',
    exec: 'Wykonanie',
    execHint: 'Koniec wcześniejszy niż początek = zmiana kończy się następnego dnia',
    from: 'Od',
    to: 'Do',
    absence: 'Nieobecność',
    absenceHint: 'Dzień nieobecności nie ma godzin pracy — wykonanie zostanie wyczyszczone',
    noAbsence: 'Brak',
    onCall: 'Dyżur (min)',
    notes: 'Uwagi',
    notesPlaceholder: 'np. praca zdalna, wyjście służbowe',
    status: 'Status wpisu',
    reason: 'Powód korekty',
    reasonRequired: 'Powód korekty — wymagany, zmieniasz istniejący wpis',
    reasonPlaceholder: 'np. korekta po rozmowie z pracownikiem',
    preview: 'Podgląd naliczenia',
    previewHint: 'Liczone na żywo tym samym silnikiem co karta',
    previewBefore: 'Teraz',
    previewAfter: 'Po korekcie',
    save: 'Zapisz korektę',
    saved: 'Zapisano korektę',
    sourceNow: 'Obecne źródło:',
    priorityNote: 'Korekta ma pierwszeństwo przed odbiciem w apce, wizytami, kartą drogową i grafikiem. Każda zmiana trafia do historii korekt.',
    errors: {
      planInvalid: 'Plan: podaj obie godziny w formacie GG:MM albo zostaw oba pola puste',
      execInvalid: 'Wykonanie: podaj obie godziny w formacie GG:MM albo zostaw oba pola puste',
      execTooLong: 'Wykonanie nie może trwać dłużej niż 24 h',
      onCallInvalid: 'Dyżur: liczba całkowita minut od 0 do 1440',
      reasonRequired: 'Podaj powód korekty',
      noChanges: 'Nic nie zmieniono',
    },
    historyTitle: 'Historia korekt',
    historyEmpty: 'Brak korekt tego dnia',
    historyLoading: 'Wczytywanie historii…',
    historyBy: '{author} · {date}',
    fields: {
      planFrom: 'Plan od',
      planTo: 'Plan do',
      execFrom: 'Wykonanie od',
      execTo: 'Wykonanie do',
      absenceCode: 'Nieobecność',
      onCallMinutes: 'Dyżur (min)',
      status: 'Status',
      notes: 'Uwagi',
    },
    empty: 'puste',
  },
  collective: {
    search: 'Szukaj pracownika lub działu…',
    department: 'Dział',
    allDepartments: 'Wszystkie działy',
    noDepartment: 'Bez działu',
    sortLabel: 'Kolejność',
    sort: {
      name: 'Nazwisko A–Z',
      balanceAsc: 'Saldo — największy niedobór',
      balanceDesc: 'Saldo — największa nadwyżka',
      worked: 'Przepracowane — najwięcej',
      alerts: 'Alerty — najwięcej',
    },
    grouped: 'Grupuj po dziale',
    colPerson: 'Pracownik',
    colWorked: 'Przepr.',
    colNominal: 'Wymiar',
    colBalance: 'Saldo',
    colOvertime: 'Nadg.',
    colAlerts: 'Alerty',
    total: 'Razem',
    groupTotal: 'Razem — {department}',
    empty: 'Brak danych za ten miesiąc',
    noMatch: 'Nikt nie pasuje do filtrów',
    cellHint: '{date}: {hours} h',
    shown: '{shown} z {total} osób',
    openHint: 'Kliknij wiersz, aby otworzyć kartę pracownika',
  },
  periods: {
    title: 'Okresy rozliczeniowe',
    description: 'Okres (1, 3 lub 4 miesiące) wyznacza wymiar czasu pracy, porę nocną i roczny limit nadgodzin. W zamkniętym okresie nie można już poprawiać wpisów.',
    add: 'Nowy okres',
    empty: 'Brak okresów rozliczeniowych',
    emptyHint: 'Dodaj pierwszy okres, aby karta liczyła wymiar i limity według Twoich ustaleń',
    cols: { name: 'Nazwa', range: 'Zakres', length: 'Długość', night: 'Pora nocna', limit: 'Limit nadgodzin', status: 'Status' },
    months: '{n} mies.',
    limit: '{h} h/rok',
    close: 'Zamknij okres',
    reopen: 'Otwórz ponownie',
    edit: 'Edytuj',
    closeConfirm: 'Zamknąć okres „{name}”? Po zamknięciu korekty wpisów w tym okresie są zablokowane.',
    closed: 'Okres zamknięty',
    reopened: 'Okres otwarty ponownie',
    saved: 'Zapisano okres',
    closedAt: 'zamknięty {date}',
    dialog: {
      addTitle: 'Nowy okres rozliczeniowy',
      editTitle: 'Edycja okresu rozliczeniowego',
      name: 'Nazwa',
      namePlaceholder: 'np. IV kwartał 2026',
      length: 'Długość okresu',
      lengthOption: '{n} mies.',
      start: 'Początek',
      end: 'Koniec (wyliczony)',
      nightStart: 'Pora nocna od (godz.)',
      nightEnd: 'Pora nocna do (godz.)',
      nightHint: 'KP art. 151⁷: 8 godzin między 21:00 a 7:00 — ustalone w regulaminie pracy',
      limit: 'Roczny limit nadgodzin (h)',
      limitHint: 'KP art. 151 §3: 150 h w roku, chyba że regulamin ustala inaczej',
      rangeLocked: 'Zakresu dat nie można zmienić po utworzeniu okresu',
      invalid: 'Uzupełnij poprawnie wszystkie pola',
    },
  },
  profiles: {
    title: 'Profile czasu pracy',
    description: 'System, etat i normy decydują, jak karta liczy wymiar i nadgodziny.',
    people: 'Osoby',
    pick: 'Wybierz osobę z listy',
    system: 'System czasu pracy',
    etat: 'Etat',
    etatHint: 'np. 1, 1/2, 3/4',
    dailyNorm: 'Norma dobowa (h)',
    weeklyNorm: 'Norma tygodniowa (h)',
    active: 'Profil aktywny',
    inactive: 'Profil nieaktywny',
    regime: 'Reżim: {regime}',
    saved: 'Zapisano profil',
    invalid: 'Sprawdź etat (np. 1 albo 1/2) i normy (0–24 h, 0–168 h)',
    systemHint: {
      BASIC: '8 h na dobę i przeciętnie 40 h w tygodniu, pięciodniowy tydzień',
      EQUIVALENT: 'Dłuższe doby (do 12 h) równoważone krótszymi dniami albo dniami wolnymi w okresie',
      TASK_BASED: 'Czas wynika z powierzonych zadań — karta pokazuje wymiar, nadgodzin się nie nalicza',
    },
  },
  my: {
    title: 'Mój czas pracy',
    description: 'Twoja karta czasu pracy i odbicie wejścia / wyjścia',
    clockIn: 'Wejście',
    clockOut: 'Wyjście',
    inWorkSince: 'W pracy od {time}',
    notInWork: 'Nie jesteś teraz w pracy',
    elapsed: 'Od wejścia minęło {duration} h',
    planToday: 'Plan na dziś',
    noPlanToday: 'Brak zmiany w grafiku',
    todayExec: 'Dziś zarejestrowano: {window}',
    clockedIn: 'Zarejestrowano wejście o {time}',
    clockedOut: 'Zarejestrowano wyjście o {time}',
    clockHint: 'Odbicie zapisuje bieżący czas. Pomyłkę poprawia kierownik korektą dnia.',
    overLimit: 'Odbicie jest otwarte ponad 16 h — poproś kierownika o korektę',
    cardTitle: 'Moja karta',
  },
  settings: {
    title: 'Reguły czasu pracy',
    description: 'Kontrola grafiku przy planowaniu zmian — progi z Kodeksu pracy, które sprawdza planer',
    chipEnforce: 'Blokada zapisu',
    chipOn: 'włączona',
    chipOff: 'wyłączona',
    chipRegime: 'Reżim',
    enforce: {
      title: 'Blokuj zapis grafiku przy naruszeniu',
      on: 'Zmiana łamiąca próg blokady nie zapisze się — planista może ją zapisać tylko z uzasadnieniem odstępstwa. Ostrzeżenia nadal tylko informują.',
      off: 'Wszystkie naruszenia są tylko ostrzeżeniami — planer je pokazuje, ale zapis zmiany jest zawsze możliwy.',
    },
    r2: {
      title: 'R2 — odpoczynek dobowy',
      law: 'KP art. 132',
      explain: 'Między końcem zmiany a początkiem zmiany następnego dnia pracownik ma mieć co najmniej 11 godzin nieprzerwanego odpoczynku. Przykład: koniec o 22:00 i start o 6:00 to tylko 8 h.',
      min: 'Minimalny odpoczynek',
      minHint: 'Krótszy odpoczynek ⇒ ostrzeżenie',
      reduced: 'Próg blokady',
      reducedHint: 'Krótszy odpoczynek ⇒ blokada (gdy włączona)',
      severityDefault: 'Domyślnie: poniżej progu blokady — blokada, między progami — ostrzeżenie',
    },
    r4: {
      title: 'R4 — tygodniowy czas pracy',
      law: 'KP art. 131',
      explain: 'Czas pracy razem z nadgodzinami nie może przekraczać przeciętnie 48 godzin w tygodniu. Planer sumuje godziny zmian od poniedziałku do niedzieli.',
      warn: 'Próg ostrzeżenia',
      warnHint: 'Więcej godzin w tygodniu ⇒ ostrzeżenie',
      block: 'Próg blokady',
      blockHint: 'Więcej godzin w tygodniu ⇒ blokada (gdy włączona)',
      severityDefault: 'Domyślnie: ponad próg blokady — blokada, ponad próg ostrzeżenia — ostrzeżenie',
    },
    r5: {
      title: 'R5 — dni pracy pod rząd',
      law: 'KP art. 133',
      explain: 'W każdym tygodniu pracownikowi przysługuje co najmniej 35 godzin nieprzerwanego odpoczynku. Długa seria dni pracy bez dnia wolnego to wyklucza.',
      max: 'Najwięcej dni pod rząd',
      maxHint: 'Dłuższa seria ⇒ ostrzeżenie',
      severityDefault: 'Domyślnie: ostrzeżenie. „Zawsze blokada” zatrzyma zbyt długie serie.',
    },
    severity: {
      label: 'Ważność',
      DEFAULT: 'Domyślna reguły',
      WARN: 'Zawsze ostrzeżenie',
      BLOCK: 'Zawsze blokada',
    },
    unitH: 'h',
    unitDays: 'dni',
    errors: {
      invalid: 'Podaj wartość od {min} do {max}',
      reducedAboveMin: 'Próg blokady nie może być wyższy niż minimalny odpoczynek',
      blockBelowWarn: 'Próg blokady nie może być niższy niż próg ostrzeżenia',
    },
    dirty: 'Masz niezapisane zmiany',
    clean: 'Wszystko zapisane',
    discard: 'Odrzuć zmiany',
    save: 'Zapisz reguły',
    saved: 'Zapisano reguły czasu pracy',
    note: 'Reguły oceniają plan (grafik) w chwili zapisu zmiany. Rzeczywiste odbicia sprawdzają alerty zgodności na karcie RCP.',
  },
  errors: {
    generic: 'Coś poszło nie tak — spróbuj ponownie',
    network: 'Brak połączenia z serwerem',
    forbidden: 'Brak uprawnień do tej operacji',
    loadFailed: 'Nie udało się wczytać danych',
    retry: 'Spróbuj ponownie',
  },
};
